import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ArtistsOverview from './ArtistsOverview';
import ArtistSettings from './ArtistSettings';
import Modal from 'components/utility/Modal/Modal';

import {
  ISubscription,
  loadSubscriptions,
  selectSubscriptions,
  shouldLoadSubscriptions,
} from 'redux/reducers/subscriptions';
import { loadCountries } from 'redux/reducers/countries';
import { loadRegions } from 'redux/reducers/regions';
import { RootState } from '../../../redux/rootReducer';
import {
  setCloseArtistSettings,
  setCloseArtistsOverview,
  setShowArtistSettings,
  setShowArtistsOverview,
  TArtist,
} from '../../../redux/reducers/notificationModals';
import getEntityMetadataByIdsURL from '../../../url-constructors/getEntityMetadataByIdsURL';
import { connect, PromiseState } from 'react-refetch';
import filterNotFound from './utils/filterNotFoundArtists';

const dataSources = (): any => {
  return {
    postArtistMetaData: (artistIds: string[]): any => ({
      postArtistMetaDataResponse: {
        method: 'POST',
        url: getEntityMetadataByIdsURL({ entityType: 'artist' }),
        body: JSON.stringify(artistIds),
      },
    }),
  };
};

const NotificationSettingsModal: React.FC<IInnerProps> = (props) => {
  const {
    postArtistMetaData,
    postArtistMetaDataResponse = PromiseState.create(),
  } = props;

  const dispatch = useDispatch();
  const [allSubscriptions, loading] = useSelector((state: RootState) =>
    selectSubscriptions(state, 'artist')
  );
  const notificationModalState = useSelector(
    (state: RootState) => state.notificationModals
  );
  const { showArtistSettings, showArtistsOverview, activeArtist } =
    notificationModalState;

  useEffect(() => {
    if (dispatch(shouldLoadSubscriptions())) {
      dispatch(loadSubscriptions());
    }
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    if (allSubscriptions?.length) {
      const artistIds = allSubscriptions.map(mapToIds);

      postArtistMetaData(artistIds);
    }

    function mapToIds(subscription: ISubscription): string {
      return subscription.entityId;
    }
  }, [postArtistMetaData, allSubscriptions]);

  useEffect(() => {
    dispatch(loadRegions());
  }, [dispatch]);

  function filterSubsForId(subscription: ISubscription): boolean {
    return !!activeArtist && subscription.entityId === activeArtist.entityId;
  }

  function closeSettings(): void {
    dispatch(setCloseArtistSettings());
  }

  function closeOverview(): void {
    dispatch(setCloseArtistsOverview());
  }

  function openSettings(artist: TArtist): void {
    dispatch(setShowArtistSettings(artist));
  }

  function openOverview(): void {
    dispatch(setShowArtistsOverview());
  }

  if (showArtistSettings && activeArtist) {
    const subscriptionsForArtist = allSubscriptions?.filter(filterSubsForId);

    return (
      <Modal hide={closeSettings} maxWidth="800px" isFullScreen>
        <ArtistSettings
          subscriptions={subscriptionsForArtist}
          artist={activeArtist}
          setShowArtistsOverview={openOverview}
          close={closeSettings}
          loading={loading}
        />
      </Modal>
    );
  }

  if (showArtistsOverview) {
    const artists = postArtistMetaDataResponse.value?.filter(filterNotFound);
    return (
      <Modal hide={closeOverview} isFullScreen={artists?.length > 5}>
        <ArtistsOverview
          artists={artists}
          setShowArtistSettings={openSettings}
          close={closeOverview}
        />
      </Modal>
    );
  }

  return null;
};

interface IInnerProps {
  postArtistMetaDataResponse: PromiseState<any>;
  postArtistMetaData: (artistIds: string[]) => any;
}

export default connect(dataSources)(NotificationSettingsModal as any);
